import { splitAndTrimAll } from 'utils/string'
import { URL_REGEX } from 'constants/url'
import {
  alphaNumMessage,
  alphaNumsMessage,
  alphaSeparatorsMessage,
  emailMessage,
  emptyMessage,
  integerMessage,
  invalidMessage,
  lengthMessage,
  longMessage,
  numberMessage,
  shortMessage
} from 'messages'
import {
  ADDRESS_MODEL_CLASS_NAME,
  ADDRESS_MODEL_KEY_LABEL,
  ADDRESS_MODEL_VALIDATOR
} from 'models/daos/address/constants'
import {
  ARTICLE_MODEL_CLASS_NAME,
  ARTICLE_MODEL_VALIDATOR
} from 'models/daos/article/constants'
import {
  CATEGORY_MODEL_CLASS_NAME,
  CATEGORY_MODEL_VALIDATOR
} from 'models/daos/category/constants'
import {
  COMMENT_MODEL_CLASS_NAME,
  COMMENT_MODEL_VALIDATOR
} from 'models/daos/comment/constants'
import { CREDENTIAL_CLASS_NAME } from 'models/daos/credential/constants'
import { CREDENTIAL_VALIDATOR } from 'modules/credential/constants'
import {
  EMAIL_TEMPLATE_MODEL_CLASS_NAME,
  EMAIL_TEMPLATE_MODEL_VALIDATOR
} from 'models/daos/email-template/constants'
import {
  LOG_MODEL_CLASS_NAME,
  LOG_MODEL_VALIDATOR
} from 'models/daos/log/constants'
import {
  PAGE_MODEL_CLASS_NAME,
  PAGE_MODEL_VALIDATOR
} from 'models/daos/page/constants'
import {
  MOTIVATION_MODEL_CLASS_NAME,
  MOTIVATION_MODEL_VALIDATOR
} from 'models/daos/motivation/constants'
import {
  ORDER_MODEL_CLASS_NAME,
  ORDER_MODEL_VALIDATOR
} from 'models/daos/order/constants/index.cjs'
import {
  PROMO_CODE_MODEL_CLASS_NAME,
  PROMO_CODE_MODEL_VALIDATOR
} from 'models/daos/promo-code/constants'
import {
  SETTINGS_MODEL_CLASS_NAME,
  SETTINGS_MODEL_VALIDATOR
} from 'models/daos/settings/constants'
import {
  PRODUCT_MODEL_CLASS_NAME,
  PRODUCT_MODEL_VALIDATOR
} from 'models/daos/product/constants'
import {
  USER_MODEL_CLASS_NAME,
  USER_MODEL_VALIDATOR
} from 'models/daos/user/constants'
import { isEmailValidatorUtil } from 'utils/validators/is-email'
import { isAlphanumericValidatorUtil } from 'utils/validators/is-alphanumeric'

/**
 * A mapping of className to key validator with length/ranges.
 * @type {Object}
 */
const MODEL_VALIDATOR = {
  [ADDRESS_MODEL_CLASS_NAME]: ADDRESS_MODEL_VALIDATOR,
  [ARTICLE_MODEL_CLASS_NAME]: ARTICLE_MODEL_VALIDATOR,
  [CATEGORY_MODEL_CLASS_NAME]: CATEGORY_MODEL_VALIDATOR,
  [COMMENT_MODEL_CLASS_NAME]: COMMENT_MODEL_VALIDATOR,
  [CREDENTIAL_CLASS_NAME]: CREDENTIAL_VALIDATOR,
  [EMAIL_TEMPLATE_MODEL_CLASS_NAME]: EMAIL_TEMPLATE_MODEL_VALIDATOR,
  [LOG_MODEL_CLASS_NAME]: LOG_MODEL_VALIDATOR,
  [PAGE_MODEL_CLASS_NAME]: PAGE_MODEL_VALIDATOR,
  [MOTIVATION_MODEL_CLASS_NAME]: MOTIVATION_MODEL_VALIDATOR,
  [ORDER_MODEL_CLASS_NAME]: ORDER_MODEL_VALIDATOR,
  [PROMO_CODE_MODEL_CLASS_NAME]: PROMO_CODE_MODEL_VALIDATOR,
  [SETTINGS_MODEL_CLASS_NAME]: SETTINGS_MODEL_VALIDATOR,
  [PRODUCT_MODEL_CLASS_NAME]: PRODUCT_MODEL_VALIDATOR,
  [USER_MODEL_CLASS_NAME]: USER_MODEL_VALIDATOR
}

/**
 * A mapping of class name to custom label.
 * @type {Object}
 */
const MODEL_KEY_LABEL = {
  [ADDRESS_MODEL_CLASS_NAME]: ADDRESS_MODEL_KEY_LABEL
}

/**
 * A mapping of key to generic validator.
 * @private
 * @type {Object}
 */
const VALIDATOR = {
  categories: isAlphaNumsWithRange.bind(null, 'categories'),
  // tags: isAlphaNumsWithRange.bind(null, 'tags'),
  contentTitle: isInLengthRangeValidatorUtil.bind(null, 'contentTitle'),
  // description: isInLengthRangeValidatorUtil.bind(null, 'description'),
  name: isInLengthRangeValidatorUtil.bind(null, 'name'),
  password: isInLengthRangeValidatorUtil.bind(null, 'password'),
  price: isFloat.bind(null, 'price'),
  // recommended: isListNumberWithRange.bind(null, 'recommended'),
  title: isInLengthRangeValidatorUtil.bind(null, 'title'),
  subject: isInLengthRangeValidatorUtil.bind(null, 'subject'),
  email,
  key,
  slug,
  website
}

/**
 * Gets the validator of the given key.
 * @param  {string} key
 * @returns {Function|null}
 */
function getValidatorUtil (key) {
  return VALIDATOR[key]
}

/**
 * Returns true if the given `value` is a valid email.
 * Otherwise it returns an error message.
 * @private
 * @param  {string} value
 * @returns {Boolean|String}
 */
function email (value) {
  if (value != null && !isEmailValidatorUtil(value)) {
    return emailMessage()
  }
  return true
}

/**
 * Returns true if the given value is a float.
 * Otherwise it returns an error message.
 *
 * @param  {string}  key
 * @param  {number}  value
 * @param  {string}  className
 * @returns {Boolean|String}
 */
function isFloat (key, value) {
  if (isNaN(parseFloat(value))) {
    return numberMessage(key)
  }
  return true
}

/**
 * Returns true if the given value is an integer.
 * Returns an error message otherwise.
 *
 * @param  {string}  key
 * @param  {number} value
 * @returns {Boolean|String}
 */
function isIntegerValidatorUtil (key, value) {
  if (!Number.isInteger(value)) {
    return integerMessage(key)
  }
  return true
}

/**
 * Returns true if the given value is in the instance's range.
 * Otherwise it returns an error message.
 * @private
 * @param  {string}  key
 * @param  {string}  value
 * @param  {string}  className
 * @returns {Boolean|String}
 */
function isInLengthRangeValidatorUtil (key, value, className) {
  const validator = MODEL_VALIDATOR[className]
  if (!validator) {
    return true
  }
  const [min, max] = validator[key]
  const label = getLabel(key, className)
  if (min && !value) {
    return emptyMessage(label)
  }
  if (value.length < min) {
    return shortMessage(label, min)
  }
  if (value.length > max) {
    return longMessage(label, max)
  }
  return true
}

/**
 * Returns true if the given value is a website.
 * Otherwise it returns an error message.
 * @private
 * @param  {string}  value
 * @returns {Boolean|String}
 */
function website (value) {
  if (!URL_REGEX.ANY_URL.test(value)) {
    return invalidMessage('website')
  }
  return true
}

/**
 * Returns true if the given value is in a slug format 'this-is-right'.
 * Otherwise it returns an error message.
 * @private
 * @param  {string}  value
 * @param  {string}  className
 * @returns {Boolean|String}
 */
function slug (value, className) {
  const range = MODEL_VALIDATOR[className].slug
  const slug = splitAndTrimAll(value, '-')
  if (!slug.every(s => isAlphanumericValidatorUtil(s))) {
    return alphaSeparatorsMessage('slug', 'dashes')
  }
  if (slug.length < range[0]) {
    return shortMessage('slug', range[0])
  }
  if (slug.length > range[1]) {
    return longMessage('slug', range[1])
  }
  return true
}

/**
 * Returns true if the given value is a valid key attribute.
 * Otherwise it returns an error message.
 * @private
 * @param  {string}  value
 * @param  {string}  className
 * @returns {Boolean|String}
 */
function key (value, className) {
  // Get the key's length
  const length = MODEL_VALIDATOR[className].key
  // If the key doesn't not only contain alphanumerics characters,
  // return an error
  if (!isAlphanumericValidatorUtil(value)) {
    return alphaNumMessage('key')
  }
  // If the key is too long or too short,
  // return an error
  if (value.length !== length) {
    return lengthMessage('key', length)
  }
  return true
}

/**
 * Returns true if the given value is a alphanumeric and is in the range.
 * Otherwise it returns an error message.
 * @private
 * @param  {string}  key
 * @param  {string}  value
 * @param  {string}  className
 * @returns {Boolean|String}
 */
function isAlphaNumsWithRange (key, value, className) {
  // Get the min/max ranges
  const validator = MODEL_VALIDATOR[className]
  if (!validator) {
    return true
  }
  const range = MODEL_VALIDATOR[className][key]
  const [min, max] = range
  // Get the values as an array
  const values = Array.isArray(value) ? value : splitAndTrimAll(value, ',')
  // Check if the array only contain alphanumerics
  const containsAlphaNums = values.every(value => {
    const valueArr = value.split(' ')
    return valueArr.every(val => isAlphanumericValidatorUtil(val))
  })
  // If one element doesn't contain an alpha numeric,
  // return an error message
  if (!containsAlphaNums) {
    return alphaNumsMessage(key)
  }
  // If there is not enough elements,
  // return an error message
  if (values.length < min) {
    return shortMessage(key, min)
  }
  // If there is too many elements,
  // return an error message
  if (values.length > max) {
    return longMessage(key, max)
  }
  return true
}

/**
 * Returns true if the given string is a list of number '42, 3, 12'.
 * Otherwise it returns an error message.
 * @private
 * @param  {string}  key
 * @param  {string}  value
 * @param  {string}  className
 * @returns {Boolean|String}
 */
// function isListNumberWithRange (key, value, className) {
//   // Get the min/max ranges
//   const validator = MODEL_VALIDATOR[className]
//   if (!validator) {
//     return true
//   }
//   const range = MODEL_VALIDATOR[className][key]
//   const [min, max] = range
//   // Get the values as an array
//   const values = Array.isArray(value) ? value : splitAndTrimAll(value, ',')
//   // Check if the array only contain numbers
//   if (!values.every(val => !isNaN(val))) {
//     return idsMessage(key)
//   }
//   // If there is not enough elements,
//   // return an error message
//   if (values.length < min) {
//     return shortMessage(key, min)
//   }
//   // If there is too many elements,
//   // return an error message
//   if (values.length > max) {
//     return longMessage(key, max)
//   }
//   return true
// }

/**
 * Returns a human readable label for the given key if available.
 * Otherwise returns the given key.
 * e.g. 'postalCode' => 'Postal Code'
 * @private
 * @param  {string} key
 * @param  {string} className
 * @returns {string}
 */
function getLabel (key, className) {
  return (
    (
      MODEL_KEY_LABEL[className] &&
      MODEL_KEY_LABEL[className][key]
    ) ||
    key
  )
}

export {
  getValidatorUtil,
  isInLengthRangeValidatorUtil,
  isIntegerValidatorUtil
}
